/**
 * Résolution du tenant par sous-domaine (multi-tenant SaaS).
 *
 * Modèle : `<slug>.suivora-app.com` = app d'un tenant (consultant + admin),
 * `console.suivora-app.com` = console éditeur (super-admin, `/console/*`),
 * `suivora-app.com`/`www.`/`staging.` = vitrine. Le slug d'org EST le sous-domaine.
 *
 * L'org n'a besoin d'être résolue depuis le domaine qu'au **login** (et flux
 * publics : reset). Une fois connecté, elle vient du JWT. Helpers purs →
 * utilisables aussi bien côté Edge (middleware) que navigateur.
 */

/** Sous-domaine dédié à la console éditeur (super-admin). */
export const CONSOLE_SUBDOMAIN = 'console';

/** Sous-domaines réservés : jamais un slug de tenant (bloqués à la création d'org côté BE). */
export const RESERVED_SUBDOMAINS = new Set(['www', 'console', 'platform', 'api', 'app', 'admin']);

/**
 * Sous-domaines qui sont l'**apex d'un déploiement**, et servent donc la vitrine
 * au même titre que le domaine nu : `www.` et l'environnement de recette
 * `staging.suivora-app.com`, sous lequel les tenants vivent un cran plus bas
 * (`<slug>.staging.suivora-app.com`).
 *
 * Liste distincte de `RESERVED_SUBDOMAINS`, qui reflète ce que le BE bloque à la
 * création d'org : `staging` n'y est pas, une org pourrait donc encore prendre ce
 * slug — elle serait alors inatteignable, puisque son hôte sert la vitrine (#820).
 */
export const VITRINE_SUBDOMAINS = new Set(['www', 'staging']);

/** Host sans port ni casse, ou `null` s'il est vide. */
function hostname(host: string | null | undefined): string | null {
  return host?.split(':')[0].trim().toLowerCase() || null;
}

/**
 * Extrait le 1er label du host s'il possède un vrai sous-domaine, sinon `null`
 * (apex `suivora-app.com`, `localhost`, IP). Gère aussi `sub.localhost` en dev.
 */
export function subdomainFromHost(host: string | null | undefined): string | null {
  const h = hostname(host);
  if (!h || h === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return null;

  const parts = h.split('.');
  const isLocal = parts[parts.length - 1] === 'localhost'; // ex. argana.localhost
  const min = isLocal ? 2 : 3; // sinon : sub.domain.tld
  if (parts.length < min) return null;

  return parts[0] || null;
}

/** Le host courant est-il la console éditeur ? */
export function isConsoleHost(host: string | null | undefined): boolean {
  return subdomainFromHost(host) === CONSOLE_SUBDOMAIN;
}

/**
 * Le host courant sert-il la vitrine ? Vrai pour l'apex d'un déploiement — le
 * domaine nu, `www.`, `staging.` — et faux dès qu'un sous-domaine porte autre
 * chose : tenant, console, sous-domaine réservé.
 *
 * `localhost` et les IP sont des apex eux aussi : la vitrine y est donc servie à
 * la racine, sans quoi le comportement de production serait invérifiable là même
 * où on le vérifie (#822). Décider si un slug forcé en développement doit primer
 * n'appartient pas à cette fonction : elle ne lit que la forme de l'hôte.
 */
export function isVitrineHost(host: string | null | undefined): boolean {
  const h = hostname(host);
  if (!h) return false;

  const sub = subdomainFromHost(h);
  return sub === null || VITRINE_SUBDOMAINS.has(sub);
}

/**
 * Slug de tenant porté par le host, ou `null` (console, sous-domaine réservé ou
 * de vitrine, apex, localhost/IP en dev mono-domaine).
 */
export function tenantSlugFromHost(host: string | null | undefined): string | null {
  const sub = subdomainFromHost(host);
  if (!sub || RESERVED_SUBDOMAINS.has(sub) || VITRINE_SUBDOMAINS.has(sub)) return null;
  return sub;
}

/**
 * Slug forcé en développement, pour travailler sur un tenant depuis `localhost`
 * sans passer par un sous-domaine.
 *
 * Trois garde-fous, parce que cette fonction décide **quel tenant** l'application
 * interroge :
 * - inerte hors `development` : la condition est statique, donc éliminée du bundle
 *   de production par Next — la variable ne peut pas fuir en ligne ;
 * - n'intervient **jamais** quand l'hôte porte déjà un sous-domaine : un vrai
 *   sous-domaine l'emporte toujours ;
 * - refuse les sous-domaines réservés ou de vitrine, jamais des tenants.
 */
function devForcedSlug(): string | null {
  if (process.env.NODE_ENV !== 'development') return null;
  const forced = process.env.NEXT_PUBLIC_DEV_TENANT_SLUG?.trim().toLowerCase();
  if (!forced || RESERVED_SUBDOMAINS.has(forced) || VITRINE_SUBDOMAINS.has(forced)) return null;
  return forced;
}

/**
 * Décision pure : slug du tenant pour un hôte donné, repli de développement inclus.
 *
 * Séparée de `currentTenantSlug` pour être testable sans manipuler
 * `window.location`, que jsdom interdit de redéfinir.
 */
export function resolveTenantSlug(hostname: string | null | undefined): string | null {
  return tenantSlugFromHost(hostname) ?? devForcedSlug();
}

/**
 * Slug du tenant courant côté navigateur (`null` en SSR / dev mono-domaine).
 *
 * En développement, `NEXT_PUBLIC_DEV_TENANT_SLUG` prend le relais quand l'hôte ne
 * porte pas de sous-domaine — sans quoi le branding de la page de connexion est
 * invisible sur `localhost` et se laisse prendre pour un bug.
 */
export function currentTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  return resolveTenantSlug(window.location.hostname);
}
